/* ===== TRKVLT Global Persistent Player + AJAX Navigation =====
   Phase 1: persistent bottom bar + AJAX navigation so audio survives page swaps.
   Phase 2: expandable full player (art, waveform, queue, repeat/shuffle,
            license/cart button, related packs).
   Phase 3: collection page + beat store wired into the same global player,
            per-track pack info (so Add to Cart / Fast Checkout on the bar
            always match whatever is currently playing), and a small
            CustomEvent bus so page-specific scripts can sync their own
            play/pause visuals without owning any audio themselves.
*/

(function () {
  'use strict';

  const PLAY_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const PAUSE_ICON = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
  const ROW_PLAY_ICON = '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const ROW_PAUSE_ICON = '<svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';
  const EXP_PLAY_ICON = '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
  const EXP_PAUSE_ICON = '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>';

  /* ---------- Global Player Controller ---------- */

  const player = {
    audio: new Audio(),
    queue: [],
    order: [],
    pos: -1,
    hasStarted: false,
    repeat: 'off', // 'off' | 'all' | 'one'
    shuffle: false,
    currentPack: null, // derived from the current track: { productId, url, variantId, price }

    init() {
      this.audio.volume = 0.8;
      this.audio.addEventListener('timeupdate', () => this.renderProgress());
      this.audio.addEventListener('loadedmetadata', () => this.renderProgress());
      this.audio.addEventListener('ended', () => this._onEnded());
      this.audio.addEventListener('play', () => {
        this.renderPlayState(true);
        window.dispatchEvent(new CustomEvent('trkvlt:audioplay', { detail: { source: 'global' } }));
      });
      this.audio.addEventListener('pause', () => this.renderPlayState(false));

      // Site-wide audio coordination: if any other audio source on the page
      // (cart-reco preview, upsell preview) starts playing, pause this player
      // too, so only one thing is ever audible at once.
      window.addEventListener('trkvlt:audioplay', (e) => {
        const source = e.detail && e.detail.source;
        if (source !== 'global' && !this.audio.paused) this.audio.pause();
      });
    },

    get currentIndex() {
      return this.pos >= 0 && this.pos < this.order.length ? this.order[this.pos] : -1;
    },

    // Each track in `queue` may carry its own pack info (productId, productUrl,
    // variantId, price) — this lets the same queue mechanism serve both a
    // single pack's tracklist (all tracks share one pack) and a page-wide
    // preview queue like the collection grid or beat store (every track is
    // its own product).
    playTrack(track, queue, index) {
      if (!track || !track.url) return;
      this.queue = (queue && queue.length) ? queue : [track];
      this.order = this.queue.map((_, i) => i);
      const startIndex = index >= 0 ? index : 0;
      if (this.shuffle) this._shuffleOrder(startIndex);
      this.pos = this.order.indexOf(startIndex);
      if (this.pos < 0) this.pos = 0;
      this._loadCurrent(true);
    },

    _loadCurrent(autoplay) {
      const track = this.queue[this.currentIndex];
      if (!track || !track.url) return;
      this.audio.src = track.url;
      if (autoplay) this.audio.play().catch(() => {});
      this.hasStarted = true;
      this.currentPack = {
        productId: track.productId || 0,
        url: track.productUrl || '',
        variantId: track.variantId || 0,
        price: track.price || ''
      };
      this.showBar();
      this.renderMeta(track);
      this.syncTracklistRows(track);
      this._buildWave(track);
      this.renderQueue();
      this.renderRepeatShuffleIcons();
      this.loadRelatedPacks();
    },

    // Keep the on-page tracklist (product page .pd-track rows) visually in
    // sync with whatever track the global player is on. Without this, the
    // pink highlight only moved on click — when a beat ended and the player
    // auto-advanced, the old row stayed highlighted. Called from
    // _loadCurrent so it covers auto-advance, next/prev buttons, and the
    // Now Playing queue as well.
    syncTracklistRows(track) {
      const rows = document.querySelectorAll('.pd-track');
      if (!rows.length || !track) return;
      rows.forEach(r => {
        const isCurrent = r.dataset.trackUrl === track.url;
        r.classList.toggle('gp-active', isCurrent);
        const btn = r.querySelector('.pd-track-btn');
        if (btn) btn.innerHTML = isCurrent ? ROW_PAUSE_ICON : ROW_PLAY_ICON;
        if (!isCurrent) {
          r.querySelectorAll('.pd-bar.played').forEach(b => b.classList.remove('played'));
        }
      });
      const activeRow = document.querySelector('.pd-track.gp-active');
      if (activeRow && typeof activeRow.scrollIntoView === 'function') {
        const rect = activeRow.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          activeRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    },

    togglePlay() {
      if (!this.hasStarted) return;
      if (this.audio.paused) this.audio.play().catch(() => {});
      else this.audio.pause();
    },

    _onEnded() {
      if (this.repeat === 'one') {
        this.audio.currentTime = 0;
        this.audio.play().catch(() => {});
        return;
      }
      this.next();
    },

    next() {
      if (this.pos < this.order.length - 1) {
        this.pos++;
        this._loadCurrent(true);
      } else if (this.repeat === 'all') {
        this.pos = 0;
        this._loadCurrent(true);
      } else {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
    },

    prev() {
      if (this.audio.currentTime > 3) {
        this.audio.currentTime = 0;
        return;
      }
      if (this.pos > 0) {
        this.pos--;
        this._loadCurrent(true);
      } else {
        this.audio.currentTime = 0;
      }
    },

    playQueuePosition(pos) {
      if (pos < 0 || pos >= this.order.length) return;
      this.pos = pos;
      this._loadCurrent(true);
    },

    toggleRepeat() {
      this.repeat = this.repeat === 'off' ? 'all' : this.repeat === 'all' ? 'one' : 'off';
      this.renderRepeatShuffleIcons();
    },

    toggleShuffle() {
      const currentTrackIndex = this.currentIndex;
      this.shuffle = !this.shuffle;
      if (this.shuffle) {
        this._shuffleOrder(currentTrackIndex);
      } else {
        this.order = this.queue.map((_, i) => i);
        this.pos = Math.max(0, this.order.indexOf(currentTrackIndex));
      }
      this.renderRepeatShuffleIcons();
      this.renderQueue();
    },

    _shuffleOrder(keepFirstIndex) {
      const indices = this.queue.map((_, i) => i);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      const pos = indices.indexOf(keepFirstIndex);
      if (pos > 0) {
        indices.splice(pos, 1);
        indices.unshift(keepFirstIndex);
      }
      this.order = indices;
      this.pos = 0;
    },

    seekFromClick(e, expanded) {
      const barId = expanded ? 'gp-exp-wave' : 'gp-progress';
      const bar = document.getElementById(barId);
      if (!bar || !this.audio.duration) return;
      const rect = bar.getBoundingClientRect();
      const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      this.audio.currentTime = pct * this.audio.duration;
    },

    setVolume(v) {
      this.audio.volume = Math.max(0, Math.min(1, v / 100));
      this.audio.muted = false;
      this._updateMuteIcon();
    },

    toggleMute() {
      this.audio.muted = !this.audio.muted;
      this._updateMuteIcon();
    },

    showBar() {
      const bar = document.getElementById('gp-bar');
      if (bar) bar.classList.add('show');
      document.body.classList.add('gp-has-player');
    },

    /* ---------- Cart actions for whatever is currently playing ---------- */

    addCurrentToCart() {
      if (!this.currentPack || !this.currentPack.variantId) return;
      if (typeof window.addToCart === 'function') window.addToCart(this.currentPack.variantId);
    },

    buyCurrentNow() {
      if (!this.currentPack || !this.currentPack.variantId) return;
      if (typeof window.buyNow === 'function') window.buyNow(this.currentPack.variantId);
    },

    /* ---------- Expanded player ---------- */

    toggleExpanded() {
      if (!this.hasStarted) return;
      const overlay = document.getElementById('gp-expand-overlay');
      const panel = document.getElementById('gp-expanded');
      if (!overlay || !panel) return;
      const isOpen = panel.classList.contains('show');
      if (isOpen) this.closeExpanded();
      else {
        overlay.classList.add('show');
        panel.classList.add('show');
        document.body.style.overflow = 'hidden';
      }
    },

    closeExpanded() {
      const overlay = document.getElementById('gp-expand-overlay');
      const panel = document.getElementById('gp-expanded');
      if (overlay) overlay.classList.remove('show');
      if (panel) panel.classList.remove('show');
      document.body.style.overflow = '';
    },

    /* ---------- Rendering ---------- */

    renderMeta(track) {
      const nameEl = document.getElementById('gp-name');
      const packEl = document.getElementById('gp-pack');
      const artEl = document.getElementById('gp-art');
      const expNameEl = document.getElementById('gp-exp-name');
      const expPackEl = document.getElementById('gp-exp-pack');
      const expArtEl = document.getElementById('gp-exp-art');
      const bgUrl = track.artwork ? `url('${track.artwork}')` : 'none';

      if (nameEl) nameEl.textContent = track.name || '';
      if (packEl) packEl.textContent = track.packTitle || '';
      if (artEl) artEl.style.backgroundImage = bgUrl;
      if (expNameEl) expNameEl.textContent = track.name || '';
      if (expPackEl) expPackEl.textContent = track.packTitle || '';
      if (expArtEl) expArtEl.style.backgroundImage = bgUrl;

      const viewBtn = document.getElementById('gp-exp-view-btn');
      if (this.currentPack) {
        if (viewBtn) viewBtn.href = this.currentPack.url || '#';
      }

      const cartBtn = document.getElementById('gp-cart-btn');
      const buyBtn = document.getElementById('gp-buy-btn');
      const hasVariant = !!(this.currentPack && this.currentPack.variantId);
      if (cartBtn) cartBtn.disabled = !hasVariant;
      if (buyBtn) buyBtn.disabled = !hasVariant;
    },

    renderPlayState(playing) {
      const btn = document.getElementById('gp-play');
      const expBtn = document.getElementById('gp-exp-play');
      if (btn) btn.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
      if (expBtn) expBtn.innerHTML = playing ? EXP_PAUSE_ICON : EXP_PLAY_ICON;
      const expArt = document.getElementById('gp-exp-art');
      if (expArt) expArt.classList.toggle('gp-exp-art-playing', playing);

      const activeRowBtn = document.querySelector('.pd-track.gp-active .pd-track-btn');
      if (activeRowBtn) activeRowBtn.innerHTML = playing ? ROW_PAUSE_ICON : ROW_PLAY_ICON;

      const track = this.queue[this.currentIndex];
      window.dispatchEvent(new CustomEvent('trkvltplayerupdate', {
        detail: { url: track ? track.url : null, playing: playing }
      }));
    },

    renderProgress() {
      const fill = document.getElementById('gp-progress-fill');
      const curEl = document.getElementById('gp-current-time');
      const durEl = document.getElementById('gp-duration');
      const expCurEl = document.getElementById('gp-exp-current-time');
      const expDurEl = document.getElementById('gp-exp-duration');

      if (this.audio.duration) {
        const pct = (this.audio.currentTime / this.audio.duration) * 100;
        if (fill) fill.style.width = pct + '%';
        if (curEl) curEl.textContent = this._formatTime(this.audio.currentTime);
        if (durEl) durEl.textContent = this._formatTime(this.audio.duration);
        if (expCurEl) expCurEl.textContent = this._formatTime(this.audio.currentTime);
        if (expDurEl) expDurEl.textContent = this._formatTime(this.audio.duration);

        const expWave = document.getElementById('gp-exp-wave');
        if (expWave) {
          expWave.querySelectorAll('.gp-exp-bar').forEach((b, i, a) => {
            b.classList.toggle('played', ((i + 1) / a.length) * 100 <= pct);
          });
        }

        const activeRow = document.querySelector('.pd-track.gp-active');
        if (activeRow) {
          activeRow.querySelectorAll('.pd-bar').forEach((b, i, a) => {
            b.classList.toggle('played', ((i + 1) / a.length) * 100 <= pct);
          });
        }

        const track = this.queue[this.currentIndex];
        window.dispatchEvent(new CustomEvent('trkvltplayerprogress', {
          detail: { url: track ? track.url : null, pct: pct }
        }));
      }
    },

    _buildWave(track) {
      const expWave = document.getElementById('gp-exp-wave');
      if (!expWave) return;
      let seed = 0;
      const str = track.url || track.name || 'x';
      for (let i = 0; i < str.length; i++) seed = (seed * 31 + str.charCodeAt(i)) % 10000;
      const bars = [];
      let x = seed * 131 + 7;
      for (let i = 0; i < 60; i++) {
        x = (x * 9301 + 49297) % 233280;
        bars.push(0.18 + (x / 233280) * 0.82);
      }
      expWave.innerHTML = bars.map(h => `<div class="gp-exp-bar" style="height:${Math.round(14 + h * 86)}%"></div>`).join('');
    },

    renderQueue() {
      const container = document.getElementById('gp-exp-queue');
      if (!container) return;
      if (this.order.length === 0) {
        container.innerHTML = '<div class="gp-exp-empty">Nothing queued.</div>';
        return;
      }
      container.innerHTML = this.order.map((trackIdx, i) => {
        const t = this.queue[trackIdx];
        const isCurrent = i === this.pos;
        return `<div class="gp-exp-queue-item${isCurrent ? ' current' : ''}" onclick="TRKVLTPlayer.playQueuePosition(${i})">
          <span class="gp-exp-queue-num">${isCurrent ? '&#9835;' : i + 1}</span>
          <span class="gp-exp-queue-name">${t.name || ''}</span>
        </div>`;
      }).join('');
    },

    renderRepeatShuffleIcons() {
      ['gp-repeat', 'gp-exp-repeat'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.toggle('active', this.repeat !== 'off');
        if (el) el.dataset.mode = this.repeat;
      });
      ['gp-shuffle', 'gp-exp-shuffle'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.toggle('active', this.shuffle);
      });
    },

    async loadRelatedPacks() {
      const container = document.getElementById('gp-exp-related');
      if (!container || !this.currentPack || !this.currentPack.productId) return;
      try {
        const resp = await fetch(window.Shopify.routes.root + 'recommendations/products?product_id=' + this.currentPack.productId + '&section_id=cart-recommendations&limit=6');
        if (!resp.ok) { container.innerHTML = ''; return; }
        const html = await resp.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = Array.from(doc.querySelectorAll('.cart-reco-item')).filter(el => {
          const pid = Number(el.dataset.productId);
          const title = (el.querySelector('.cart-reco-title')?.textContent || '').toLowerCase();
          return pid !== this.currentPack.productId && !title.includes('stem') && !title.includes('unlimited');
        }).slice(0, 4);
        container.innerHTML = '';
        items.forEach(el => container.appendChild(el));
      } catch (err) {
        console.error('Related packs failed:', err);
        container.innerHTML = '';
      }
    },

    _formatTime(s) {
      if (!isFinite(s)) return '0:00';
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60).toString().padStart(2, '0');
      return m + ':' + sec;
    },

    _updateMuteIcon() {
      const btn = document.getElementById('gp-mute');
      if (!btn) return;
      const muted = this.audio.muted || this.audio.volume === 0;
      btn.innerHTML = muted
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/></svg>';
    }
  };

  window.TRKVLTPlayer = player;
  player.init();

  /* ---------- Product tracklist -> global player wiring ---------- */

  window.playTrackGlobal = function (el) {
    const url = el.dataset.trackUrl;
    if (!url) return;

    // Clicking the currently-playing row just toggles play/pause, matching
    // the old per-page behavior instead of restarting the track.
    const current = player.queue[player.currentIndex];
    if (el.classList.contains('gp-active') && current && current.url === url) {
      player.togglePlay();
      return;
    }

    const container = el.closest('#pd-tracklist') || el.closest('.pd-tracklist');
    const rows = container ? Array.from(container.querySelectorAll('.pd-track')) : [el];
    const packTitleEl = document.querySelector('.pd-title');
    const packArtEl = document.querySelector('.pd-image img');
    const packTitle = packTitleEl ? packTitleEl.textContent.trim() : '';
    const artwork = packArtEl ? packArtEl.src : '';

    const mainEl = document.getElementById('main-content');
    const packInfo = {
      productId: Number(mainEl?.dataset.productId) || 0,
      productUrl: container ? container.dataset.productUrl : '',
      variantId: container ? Number(container.dataset.variantId) || 0 : 0,
      price: container ? container.dataset.price : ''
    };

    const queue = rows.map(r => ({
      name: r.dataset.trackName,
      url: r.dataset.trackUrl,
      packTitle: packTitle,
      artwork: artwork,
      ...packInfo
    })).filter(t => t.url);

    const index = rows.indexOf(el);
    if (queue.length === 0) return;

    rows.forEach(r => r.classList.remove('gp-active'));
    el.classList.add('gp-active');

    player.playTrack(queue[index >= 0 ? index : 0], queue, index >= 0 ? index : 0);
  };

  /* ---------- Collection page / "pack preview" grid wiring -----------
     Generic helper for any grid of .bp-card elements with a .bp-morph
     play button, each card carrying its own pack data attributes. Used
     by the beat pack collection page; queues every currently-visible
     card so next/prev cycle through the grid. */

  window.playPackPreview = function (morphEl) {
    const url = morphEl.dataset.sample;
    if (!url) return;

    // If this exact track is already loaded (playing or paused), just
    // toggle play/pause instead of reloading it from the start — otherwise
    // clicking "pause" looked identical to clicking "play" and always
    // restarted the track.
    const current = player.queue[player.currentIndex];
    if (player.hasStarted && current && current.url === url) {
      player.togglePlay();
      return;
    }

    const clickedCard = morphEl.closest('.bp-card');
    const grid = clickedCard ? clickedCard.closest('#bp-grid, .bp-grid') : null;
    const cards = grid
      ? Array.from(grid.querySelectorAll('.bp-card')).filter(c => c.style.display !== 'none')
      : (clickedCard ? [clickedCard] : []);

    const queue = cards.map(card => {
      const m = card.querySelector('.bp-morph[data-sample]');
      const img = card.querySelector('.bp-img img');
      return {
        name: card.dataset.title || '',
        url: m ? m.dataset.sample : '',
        packTitle: 'Beat Pack Preview',
        artwork: img ? img.src : '',
        productId: Number(card.dataset.productId) || 0,
        productUrl: card.dataset.productUrl || '',
        variantId: Number(card.dataset.variantId) || 0,
        price: card.dataset.price || ''
      };
    }).filter(t => t.url);

    if (queue.length === 0) return;
    const index = cards.indexOf(clickedCard);
    const queueIndex = index >= 0 ? queue.findIndex((t, i) => cards[i] === clickedCard) : 0;

    player.playTrack(queue[queueIndex >= 0 ? queueIndex : 0], queue, queueIndex >= 0 ? queueIndex : 0);
  };

  /* ---------- Related-pack cards (cart drawer + Now Playing) ----------
     The same .cart-reco-item markup is reused in the cart drawer and in
     the expanded player's "Related Beat Packs" list. In the cart drawer a
     quick single-sample preview (toggleCartRecoAudio, in theme.js) is fine.
     But inside the Now Playing screen, clicking play should behave like
     clicking any other pack: queue that pack's FULL tracklist into the
     global player so next/prev and the tracklist view all work. */

  window.handleRecoPlayClick = function (btn) {
    const item = btn.closest('.cart-reco-item');
    const inNowPlaying = !!btn.closest('#gp-exp-related');

    if (!inNowPlaying || !item) {
      if (typeof window.toggleCartRecoAudio === 'function') window.toggleCartRecoAudio(btn);
      return;
    }

    let tracks = [];
    try { tracks = JSON.parse(item.dataset.tracks || '[]'); } catch (e) { tracks = []; }

    if (!Array.isArray(tracks) || tracks.length === 0) {
      if (typeof window.toggleCartRecoAudio === 'function') window.toggleCartRecoAudio(btn);
      return;
    }

    const packTitle = item.dataset.title || '';
    const artwork = item.dataset.image || '';
    const packInfo = {
      productId: Number(item.dataset.productId) || 0,
      productUrl: item.dataset.productUrl || '',
      variantId: Number(item.dataset.variantId) || 0,
      price: item.dataset.price || ''
    };

    const queue = tracks.map(t => ({
      name: t.name,
      url: t.url,
      packTitle: packTitle,
      artwork: artwork,
      ...packInfo
    })).filter(t => t.url);

    if (queue.length === 0) {
      if (typeof window.toggleCartRecoAudio === 'function') window.toggleCartRecoAudio(btn);
      return;
    }

    player.playTrack(queue[0], queue, 0);
  };

  /* ---------- AJAX Navigation Layer ---------- */

  function isSameOrigin(url) {
    try {
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function shouldIntercept(link) {
    if (!link || !link.href) return false;
    if (link.target && link.target !== '' && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.dataset.noAjax !== undefined) return false;
    if (!isSameOrigin(link.href)) return false;

    const path = new URL(link.href, window.location.href).pathname;
    if (/^\/(checkout|admin|account\/login)/.test(path)) return false;

    const linkNoHash = link.href.split('#')[0];
    const currentNoHash = window.location.href.split('#')[0];
    if (link.href.indexOf('#') !== -1 && linkNoHash === currentNoHash) return false; // same-page anchor

    return true;
  }

  let navToken = 0;

  function showNavLoader() {
    const el = document.getElementById('gp-nav-loader');
    if (!el) return;
    el.style.transition = 'none';
    el.style.width = '0%';
    el.style.opacity = '1';
    requestAnimationFrame(() => {
      el.style.transition = 'width 0.4s ease-out';
      el.style.width = '70%';
    });
  }

  function hideNavLoader() {
    const el = document.getElementById('gp-nav-loader');
    if (!el) return;
    el.style.transition = 'width 0.2s ease-out';
    el.style.width = '100%';
    setTimeout(() => { el.style.opacity = '0'; }, 200);
  }

  async function navigateTo(url, pushState) {
    const token = ++navToken;
    showNavLoader();
    if (player.hasStarted) player.closeExpanded();
    const navToggle = document.getElementById('nav-toggle');
    if (navToggle) navToggle.checked = false;
    try {
      const resp = await fetch(url, { headers: { 'X-Requested-With': 'trkvlt-ajax' } });
      if (!resp.ok) { window.location.href = url; return; }
      const html = await resp.text();
      if (token !== navToken) return; // a newer navigation started before this one finished

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newMain = doc.getElementById('main-content');
      const curMain = document.getElementById('main-content');
      if (!newMain || !curMain) { window.location.href = url; return; }

      curMain.innerHTML = newMain.innerHTML;
      curMain.dataset.productId = newMain.dataset.productId || '0';
      curMain.dataset.stemsId = newMain.dataset.stemsId || '0';
      curMain.dataset.unlimitedId = newMain.dataset.unlimitedId || '0';
      window.CURRENT_PRODUCT_ID = Number(curMain.dataset.productId) || 0;
      window.PRODUCT_STEMS_ID = Number(curMain.dataset.stemsId) || 0;
      window.PRODUCT_UNLIMITED_ID = Number(curMain.dataset.unlimitedId) || 0;

      // innerHTML doesn't execute injected <script> tags, so replace each one
      // with a fresh element to force the browser to run it.
      curMain.querySelectorAll('script').forEach(oldScript => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
        newScript.textContent = oldScript.textContent;
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      if (doc.title) document.title = doc.title;
      if (pushState) history.pushState({ trkvltAjax: true }, '', url);
      window.scrollTo(0, 0);

      if (typeof window.trkvltPageInit === 'function') window.trkvltPageInit();
    } catch (err) {
      console.error('AJAX navigation failed, falling back to full page load:', err);
      window.location.href = url;
    } finally {
      if (token === navToken) hideNavLoader();
    }
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('a');
    if (!shouldIntercept(link)) return;
    e.preventDefault();
    navigateTo(link.href, true);
  });

  window.addEventListener('popstate', function () {
    navigateTo(window.location.href, false);
  });
})();
